// A file on the composer, drawn the way this app draws a document anywhere
// else: a small white sheet with a sliver of coral tape along its foot — the
// same mark .paper-chip-thumb uses so an inline reference still reads as paper.
//
// The tape is the progress. It fills left to right while the direct upload
// runs and stands full once the blob is stored, so the chip becomes a proper
// document at exactly the moment it becomes one. Both states used to be built
// separately — a plain badge counting a percentage up, then a differently
// shaped green badge — so a file changed form the instant it landed.

const KIND_GLYPHS = {
  pdf: "ti-file-type-pdf",
  csv: "ti-file-type-csv",
  xls: "ti-file-type-xls",
  xlsx: "ti-file-type-xls",
  doc: "ti-file-type-doc",
  docx: "ti-file-type-doc",
  txt: "ti-file-type-txt",
  xml: "ti-file-type-xml",
  json: "ti-json",
  png: "ti-photo",
  jpg: "ti-photo",
  jpeg: "ti-photo",
  gif: "ti-photo",
  webp: "ti-photo",
  heic: "ti-photo",
  zip: "ti-file-zip"
}

export function formatFileSize(bytes) {
  if (!Number.isFinite(bytes)) return ""
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1048576).toFixed(1)} MB`
}

export function glyphFor(filename) {
  const ext = String(filename || "").split(".").pop().toLowerCase()
  return KIND_GLYPHS[ext] || "ti-file"
}

// `state` is "uploading" | "attached" | "failed".
export function buildUploadChip({ id, filename, byteSize, state = "uploading" }) {
  const chip = document.createElement("span")
  chip.className = "upload-chip"
  chip.id = id
  chip.dataset.state = state
  chip.title = filename


  const icon = document.createElement("i")
  icon.className = `ti ${glyphFor(filename)} upload-chip__glyph`
  icon.setAttribute("aria-hidden", "true")
  chip.appendChild(icon)

  const name = document.createElement("span")
  name.className = "upload-chip__name file-name"
  name.textContent = filename
  chip.appendChild(name)

  const meta = document.createElement("span")
  meta.className = "upload-chip__meta file-percent"
  meta.textContent = state === "uploading" ? "0%" : formatFileSize(byteSize)
  chip.appendChild(meta)

  // The tape along the foot. Laid in last so it sits over the sheet's edge the
  // way a real band would.
  const tape = document.createElement("span")
  tape.className = "upload-chip__tape"
  const fill = document.createElement("span")
  fill.className = "upload-chip__tape-fill"
  fill.style.transform = state === "uploading" ? "scaleX(0)" : "scaleX(1)"
  tape.appendChild(fill)
  chip.appendChild(tape)

  return chip
}

export function setChipProgress(chip, percentage) {
  if (!chip) return

  const clamped = Math.max(0, Math.min(100, percentage))
  const fill = chip.querySelector(".upload-chip__tape-fill")
  const meta = chip.querySelector(".upload-chip__meta")

  if (fill) fill.style.transform = `scaleX(${clamped / 100})`
  if (meta) meta.textContent = `${Math.round(clamped)}%`
}

// Only meaningful once the file is actually attached.
export function addRemoveButton(chip, onRemove) {
  const button = document.createElement("button")
  button.type = "button"
  button.className = "upload-chip__remove"
  button.title = "Remove attachment"
  button.setAttribute(
    "aria-label",
    `Remove ${chip.querySelector(".upload-chip__name")?.textContent || "attachment"}`
  )
  button.innerHTML = '<i class="ti ti-x" aria-hidden="true"></i>'
  button.addEventListener("click", onRemove)
  chip.appendChild(button)
  return button
}
